import { QuestionModel } from '@/modules/core/domain/interfaces/IFormulary';
import useField from '@/modules/WebForm/hooks/useField';
import { createAnswer } from '@/modules/WebForm/utils';
import { parseFieldConditionals } from '@/modules/WebForm/utils/parseFieldConditionals';
import debounce from '@/utils/debounce';
import {
  Box,
  Flex,
  HStack,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Tag,
} from '@chakra-ui/react';
import React from 'react';

import AnswerHistory from '../AnswerHistory';
import FormControlComponent from '../FormControlComponent';

const format = (val: string) => `${val}`;
const parse = (val: string) => val.replace(/[^0-9.]/g, '');

const CurrencyContent: React.FC<QuestionModel> = ({ id, formularyId }) => {
  const { error, onChange, fieldState, alert, field, clear } = useField();

  const [value, setValue] = React.useState(() => {
    if (fieldState && !Array.isArray(fieldState)) {
      const num = parseFloat(fieldState?.content);
      if (!Number.isNaN(num)) {
        return Math.max(0, num).toFixed(2);
      }
    }
    return '';
  });

  const update = React.useRef(
    debounce((value: string) => {
      const numValue = Math.max(0, parseFloat(value) || 0);
      const answer = createAnswer(numValue, undefined, undefined, undefined, 'money');
      const conditionalResult = parseFieldConditionals(field, answer);
      const isInvalid = conditionalResult.error;

      if (isInvalid) {
        clear();
        setValue('');
      }
    }, 500),
  );

  const handleBlur = React.useCallback(() => {
    const numValue = Math.max(0, parseFloat(value) || 0);
    onChange(createAnswer(numValue, undefined, undefined, undefined, 'money'));
    setValue(numValue.toFixed(2));
  }, [value, onChange]);

  const handleFocus = React.useCallback(() => {
    if (value.length === 0) {
      setValue('0.00');
      onChange(createAnswer(0.0, undefined, undefined, undefined, 'money'));
    }
  }, [value.length, onChange]);

  const onChangeText = React.useCallback(
    (valueString: string, _valueNumber: number) => {
      const answer = `${parse(valueString)}`;
      setValue(answer);
      update.current(answer);
    },
    [],
  );

  const handleSelect = React.useCallback(
    (selectedValue: string) => {
      const num = Math.max(0, parseFloat(selectedValue) || 0);
      const formatted = num.toFixed(2);
      setValue(formatted);
      onChange(createAnswer(num, undefined, undefined, undefined, 'money'));
    },
    [onChange],
  );

  return (
    <Flex w="full" align="flex-start">
      <FormControlComponent isInvalid={error}>
        <HStack alignItems="center">
          <Box>R$</Box>
          <NumberInput
            onChange={onChangeText}
            value={format(value)}
            min={0}
            step={0.5}
            precision={2}
            position="relative"
            onFocus={handleFocus}
            onBlur={handleBlur}
          >
            <NumberInputField />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
        </HStack>
        <Tag size="lg" colorScheme="red" mt="4" hidden={!alert}>
          {alert}
        </Tag>
      </FormControlComponent>
      <AnswerHistory
        questionId={id}
        formularyId={formularyId}
        onSelect={handleSelect}
      />
    </Flex>
  );
};

export default CurrencyContent;
